import { useEffect, useRef } from "react";
import * as THREE from "three";
import { buildShip, createRenderer, createWorld, halfBeamAt, projectTo } from "@/components/three/maritime";
import { buildLinks, buildShore } from "@/components/three/shore";

const KEYS = [
  { p: 0.0, pos: [-2, 15, 64], look: [-14, 5, 0] },
  { p: 0.26, pos: [-10, 17, 60], look: [-18, 6, 0] },
  { p: 0.5, pos: [-46, 28, 50], look: [-4, 3, 0] },
  { p: 0.72, pos: [35, 38, 82], look: [-8, 3, -35] },
  { p: 1.0, pos: [96, 78, 150], look: [-40, 6, -150] },
];
const FOG_NEAR = 0.0046;
const FOG_FAR = 0.0022;
const smooth = (a, b, x) => {
  const t = Math.min(Math.max((x - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
};
const lerp3 = (a, b, t, out) => out.set(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t);

const cameraAt = (p, pos, look) => {
  let i = 0;
  while (i < KEYS.length - 2 && p > KEYS[i + 1].p) i++;
  const a = KEYS[i];
  const b = KEYS[i + 1];
  const t = smooth(a.p, b.p, p);
  lerp3(a.pos, b.pos, t, pos);
  lerp3(a.look, b.look, t, look);
};

export const TAGS = [
  { id: "bridge", label: "Bridge & accommodation", from: 0, to: 0.24 },
  { id: "lifeboat", label: "Lifeboat", from: 0, to: 0.24 },
  { id: "crew", label: "Crew onboard: 0", from: 0.4, to: 0.5 },
  { id: "core", label: "Onboard intelligence", from: 0.53, to: 0.66 },
  { id: "robot", label: "Maintenance robot", from: 0.64, to: 0.75 },
  { id: "fleet", label: "Connected fleet", from: 0.88, to: 1.01 },
  { id: "roc", label: "Mariners ashore · ROC", from: 0.61, to: 1.01 },
];

function buildNetwork(ship) {
  const core = new THREE.Group();
  const coreMat = new THREE.MeshBasicMaterial({ color: 0x00f2fe, wireframe: true, transparent: true, opacity: 0.8 });
  const coreGeo = new THREE.IcosahedronGeometry(1.5, 1);
  const inner = new THREE.Mesh(new THREE.SphereGeometry(0.55, 16, 12), new THREE.MeshBasicMaterial({ color: 0xe8feff }));
  core.add(new THREE.Mesh(coreGeo, coreMat), inner);
  core.position.set(0, 12, 0);
  const pos = [];
  ship.sensors.forEach((s) => pos.push(s.node.position.x, s.node.position.y, s.node.position.z, 0, 12, 0));
  const lineGeo = new THREE.BufferGeometry();
  lineGeo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  const lineMat = new THREE.LineBasicMaterial({ color: 0x00f2fe, transparent: true, opacity: 0 });
  const lines = new THREE.LineSegments(lineGeo, lineMat);
  ship.group.add(core, lines);
  return { core, coreMat, lineMat, disposables: [coreMat, coreGeo, inner.geometry, inner.material, lineGeo, lineMat] };
}

function buildRobots(ship) {
  const geo = new THREE.BoxGeometry(0.9, 0.42, 0.5);
  const mat = new THREE.MeshBasicMaterial({ color: 0x00f2fe, transparent: true, opacity: 0 });
  const robots = [0, 1, 2].map((i) => {
    const m = new THREE.Mesh(geo, mat);
    m.userData = { side: i % 2 ? 1 : -1, phase: i * 2.1, speed: 0.12 + i * 0.03 };
    ship.group.add(m);
    return m;
  });
  return { robots, mat, disposables: [geo, mat] };
}

export const TransformScene = ({ progress, className = "", tagRefs }) => {
  const mount = useRef(null);

  useEffect(() => {
    const el = mount.current;
    if (!el) return undefined;
    let mobile = window.matchMedia("(max-width: 767px)").matches;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let dirty = true;
    let lastRenderedProgress = -1;
    let renderer;
    try {
      renderer = createRenderer(el, mobile);
    } catch (e) {
      return undefined;
    }
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(mobile ? 52 : 34, 1, 0.5, 1400);
    const world = createWorld(scene, { segments: mobile ? 110 : 180, rings: 0, sweep: 0, grid: 0.5, fogDensity: FOG_NEAR });
    const ship = buildShip({ accommodation: true, extraCargo: true });
    scene.add(ship.group);
    const net = buildNetwork(ship);
    const bots = buildRobots(ship);
    const fleet = [buildShip({ seed: 3 }), buildShip({ seed: 19 }), buildShip({ seed: 11 })];
    const fleetHome = [new THREE.Vector3(-80, 0, -95), new THREE.Vector3(55, 0, -170), new THREE.Vector3(-30, 0, -250)];
    fleet.forEach((f) => scene.add(f.group));
    const shore = buildShore(scene);
    const links = buildLinks(scene, fleet.length + 1, shore.halo);
    const masts = [ship, ...fleet].map((s) => s.aftTop.clone().add(new THREE.Vector3(0, 1.2, 0)));
    const starts = masts.map(() => new THREE.Vector3());
    const acc = ship.accommodation;
    const tagPoints = [
      { v: new THREE.Vector3(-25.6, 12.4, 0), obj: ship.group },
      { v: new THREE.Vector3(-31.6, 5, 0), obj: ship.group },
      { v: new THREE.Vector3(-26, 4, 0), obj: ship.group },
      { v: new THREE.Vector3(0, 14, 0), obj: ship.group },
      { v: new THREE.Vector3(0, 0.8, 4.8), obj: bots.robots[0] },
      { v: new THREE.Vector3(0, 9, 0), obj: fleet[0].group },
      { v: new THREE.Vector3(0, 30, 12), obj: shore.group },
    ];

    let w = 1;
    let h = 1;
    const resize = () => {
      w = el.clientWidth || 1;
      h = el.clientHeight || 1;
      mobile = w < 768;
      dirty = true;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.fov = mobile ? 52 : THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(34) / 2) * Math.max(1, 1.5 / camera.aspect)));
      if (mobile) camera.setViewOffset(w, h, 0, h * 0.22, w, h);
      else camera.setViewOffset(w, h, -w * 0.17, 0, w, h);
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    let visible = false;
    const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; });
    io.observe(el);

    const clock = new THREE.Clock();
    const camPos = new THREE.Vector3();
    const look = new THREE.Vector3();
    const tmp = new THREE.Vector3();
    let smoothP = 0;
    let lastT = 0;
    let raf = 0;

    const frame = () => {
      const t = reducedMotion ? 0 : clock.getElapsedTime();
      const dt = Math.min(t - lastT, 0.1);
      lastT = t;
      const target = Math.min(Math.max(progress?.get?.() ?? 0, 0), 1);
      smoothP = reducedMotion ? target : smoothP + (target - smoothP) * (1 - Math.exp(-dt * 7));
      lastRenderedProgress = target;
      dirty = false;
      const p = smoothP;
      world.update(t);
      ship.update(t);

      const k5 = smooth(0.76, 0.9, p);
      fleet.forEach((f, i) => {
        f.group.visible = k5 > .01;
        f.update(t + i * 3);
        f.group.position.x = fleetHome[i].x;
        f.group.position.z = fleetHome[i].z - (1 - k5) * 320;
        f.setSensorLevel(k5, t);
      });

      const k1 = smooth(0.27, 0.42, p);
      acc.acc.position.y = k1 * 7;
      acc.mats.forEach((m) => { m.opacity = 1 - k1; });
      acc.acc.visible = k1 < 0.995;
      ship.extra.scale.y = Math.max(smooth(0.38, 0.5, p), 0.001);

      const k3 = smooth(0.52, 0.62, p);
      ship.setSensorLevel(k3, t);
      net.core.scale.setScalar(Math.max(k3, 0.001));
      net.core.rotation.y = t * 0.4;
      net.lineMat.opacity = k3 * 0.38 * (0.75 + 0.25 * Math.sin(t * 3));
      world.uniforms.uRings.value = k3;
      world.uniforms.uSweep.value = k3;

      const k4 = smooth(0.6, 0.7, p);
      bots.mat.opacity = k4;
      bots.robots.forEach((b) => {
        const { side, phase, speed } = b.userData;
        const x = Math.sin(t * speed + phase) * 22;
        b.position.set(x, 0.3, side * (halfBeamAt(x) - 0.6));
        b.rotation.y = Math.cos(t * speed + phase) > 0 ? 0 : Math.PI;
      });

      const kF = smooth(0.78, 0.9, p);
      const fog = FOG_NEAR + (FOG_FAR - FOG_NEAR) * kF;
      scene.fog.density = fog;
      world.uniforms.uFogDensity.value = fog;
      const shoreVisible = smooth(0.53, 0.61, p);
      shore.group.position.set(-35 - 115 * kF, 0, -100 - 330 * kF);
      shore.beacon.copy(shore.group.position).add(new THREE.Vector3(0, 15, 12));
      shore.update(shoreVisible, t);
      [ship, ...fleet].forEach((s, i) => starts[i].copy(masts[i]).add(s.group.position));
      links.update(starts, shore.beacon, smooth(0.56, 0.66, p), t, k5 > .5 ? 4 : 1);

      cameraAt(p, camPos, look);
      if (mobile) {
        look.x = -40 * smooth(0.76, 1, p);
        camPos.sub(look).multiplyScalar(2.4 - 0.75 * smooth(0.76, 1, p)).add(look);
      }
      camera.position.copy(camPos);
      camera.lookAt(look);
      renderer.render(scene, camera);

      tagPoints.forEach((tp, i) => {
        const node = tagRefs?.current?.[i];
        if (!node) return;
        const s = projectTo(tp.v, tp.obj, camera, w, h, tmp);
        const tag = TAGS[i];
        const on = p >= tag.from && p < tag.to && !s.behind;
        node.style.transform = `translate3d(${Math.max(150, Math.min(w - 150, s.x))}px, ${Math.max(115, Math.min(h - 20, s.y))}px, 0)`;
        node.style.opacity = on ? "1" : "0";
      });
    };
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (visible && !document.hidden && (!reducedMotion || dirty || progress.get() !== lastRenderedProgress)) frame();
    };
    frame();
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      world.dispose();
      ship.dispose();
      fleet.forEach((f) => f.dispose());
      shore.dispose();
      links.dispose();
      [...net.disposables, ...bots.disposables].forEach((d) => d.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [progress, tagRefs]);

  return <div ref={mount} className={className} aria-hidden="true" data-testid="transform-3d-scene" />;
};
